<template>
  <div class="manual-avr-modal">
    <div class="manual-avr-modal__scroll">
      <CustomTable 
        :rowsData="this.callsData" 
        :columns="columns" 
        :custom-body="true" 
        :custom-header="true"
        :striped="true"
        :hoverless="true" 
        :bordered="true" 
        row-key="help_call_id" 
        :loading="loading"
        :sticky-header="false"
        :bottomRow="selectedCallIds.length > 0"
      >
        <template #customHeader="props">
          <q-tr>
            <q-th auto-width>
              <q-checkbox
                :model-value="isAllSelected"
                :indeterminate="isIndeterminate"
                :disable="selectableCallIds.length === 0"
                @update:model-value="selectAll"
              />
            </q-th>
            <q-th v-for="col in props.cols" :key="col.name" :props="props">
              {{ col.label }}
            </q-th>
          </q-tr>
        </template>
        <template #customBody="props">
          <q-tr
            :props="props"
            :class="{ 'manual-avr-modal__row--blocked': !isK2svRowSelectable(props.row) }"
          >
            <CustomTableCell align="center" vertical-align="center">
              <CustomCheckbox
                :value="isItemSelected(props.row.help_call_id)"
                :disable="!isK2svRowSelectable(props.row)"
                @change="(value) => toggleSelectItem(props.row.help_call_id, value)"
              />
            </CustomTableCell>
            <CustomTableCell align="center" vertical-align="center">
              {{ props.row.entrance }}
            </CustomTableCell>
            <CustomTableCell align="center" vertical-align="center">
              {{ props.row.level_num }}
            </CustomTableCell>
            <CustomTableCell align="left" vertical-align="center">
              {{ props.row.placement_type_name }}
            </CustomTableCell>
            <CustomTableCell align="left" vertical-align="center">
              {{ props.row.work_set_check_group_name }}
            </CustomTableCell>
            <CustomTableCell align="left" vertical-align="center" class="k2sv-hints-cell">
              <span v-if="isK2svRowSelectable(props.row)" class="k2sv-hints k2sv-hints--ok">Готово к КС2В</span>
              <span v-else class="k2sv-hints">{{ formatK2svMissing(props.row) }}</span>
            </CustomTableCell>
            <CustomTableCell align="right" vertical-align="center">
              <Amount :value="props.row.call_sum_without_vat" />
            </CustomTableCell>
            <CustomTableCell align="center" vertical-align="center">
              {{ formatNdsPercent(props.row.nds_percent) }}
            </CustomTableCell>
            <CustomTableCell align="right" vertical-align="center">
              <Amount :value="props.row.call_sum" />
            </CustomTableCell>
          </q-tr>
        </template>
        <template #bottomRow v-if="selectedCallIds.length > 0">
          <q-tr class="total-row">
            <CustomTableCell align="right" vertical-align="center" colspan="6">
              <strong>Сумма по вызовам (КС2В):</strong>
            </CustomTableCell>
            <CustomTableCell align="right" vertical-align="center">
              <strong><Amount :value="selectedCallsSumWithoutVat" /></strong>
            </CustomTableCell>
            <CustomTableCell align="center" vertical-align="center">
              <strong>{{ selectedCallsNdsFooter }}</strong>
            </CustomTableCell>
            <CustomTableCell align="right" vertical-align="center">
              <strong><Amount :value="selectedCallsSumWithVat" /></strong>
            </CustomTableCell>
          </q-tr>
        </template>
      </CustomTable>

      <div v-if="selectedCallIds.length > 0" class="manual-avr-modal__materials">
        <CustomTable 
          :rowsData="this.materialsData" 
          :columns="materialsColumns" 
          :custom-body="true" 
          :striped="true"
          :hoverless="true" 
          :bordered="true" 
          row-key="material_id" 
          :loading="materialsLoading"
          :sticky-header="false"
        >
          <template #customBody="props">
            <q-tr :props="props">
              <CustomTableCell align="left" vertical-align="center">
                {{ props.row.material_name }}
              </CustomTableCell>
              <CustomTableCell align="center" vertical-align="center">
                {{ props.row.material_unit_name }}
              </CustomTableCell>
              <CustomTableCell align="right" vertical-align="center">
                {{ props.row.material_amount }}
              </CustomTableCell>
              <CustomTableCell align="right" vertical-align="center">
                <Amount :value="props.row.material_price" />
              </CustomTableCell>
              <CustomTableCell align="right" vertical-align="center">
                <Amount :value="props.row.total_material_sum" />
              </CustomTableCell>
            </q-tr>
          </template>
        </CustomTable>
      </div>
    </div>

    <div class="manual-avr-modal__dock">
      <div v-if="selectedCallIds.length > 0" class="manual-avr-modal__preview">
        <div class="manual-avr-modal__preview-label">Предпросмотр печатной формы КС2В</div>
        <CustomButton
          @click="openK2svPreviewPdf"
          color="primary"
          :loading="downloadingPreview"
          :disabled="downloadingPreview"
          style="width: auto; padding: 8px 16px; font-size: 14px;"
        >
          Открыть
        </CustomButton>
      </div>

      <div class="manual-avr-modal__footer">
        <CustomButton 
          @click="createAvr" 
          color="primary" 
          :loading="creatingAvr"
          :disabled="selectedCallIds.length === 0 || creatingAvr"
        >
          Создать КС2В
        </CustomButton>
      </div>
    </div>
  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomButton from "@/components/common/CustomButton.vue";
import Amount from "@/components/common/Amount.vue";
import { getManualAvrCalls, getManualAvrMaterials, createManualAvr, openManualPreviewInTab } from "@/views/ResidentialSettings/services";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: 'ManualAvrCalls',
  components: { CustomTable, CustomTableCell, CustomCheckbox, CustomButton, Amount },
  props: ['data'],
  data() {
    return {
      columns: [
        {
          name: 'entrance',
          label: 'Подъезд',
          align: 'center',
          field: 'entrance',
          sortable: true,
        },
        {
          name: 'level_num',
          label: 'Этаж',
          align: 'center',
          field: 'level_num',
          sortable: true,
        },
        {
          name: 'placement_type_name',
          label: 'Тип помещение',
          align: 'left',
          field: 'placement_type_name',
          sortable: true,
        },
        {
          name: 'work_set_check_group_name',
          label: 'Группа работ',
          align: 'left',
          field: 'work_set_check_group_name',
          sortable: true,
        },
        {
          name: 'k2sv_readiness',
          label: 'Условия КС2В',
          align: 'left',
          field: 'k2sv_readiness',
          sortable: false,
        },
        {
          name: 'call_sum_without_vat',
          label: 'Сумма без НДС',
          align: 'right',
          field: 'call_sum_without_vat',
          sortable: true,
        },
        {
          name: 'nds_percent',
          label: 'НДС, %',
          align: 'center',
          field: 'nds_percent',
          sortable: true,
        },
        {
          name: 'call_sum',
          label: 'Сумма с НДС',
          align: 'right',
          field: 'call_sum',
          sortable: true,
        },
      ],
      materialsColumns: [
        {
          name: 'material_name',
          label: 'Материал',
          align: 'left',
          field: 'material_name',
          sortable: true,
        },
        {
          name: 'material_unit_name',
          label: 'Ед изм',
          align: 'center',
          field: 'material_unit_name',
          sortable: true,
        },
        {
          name: 'material_amount',
          label: 'Количество',
          align: 'right',
          field: 'material_amount',
          sortable: true,
        },
        {
          name: 'material_price',
          label: 'Цена',
          align: 'right',
          field: 'material_price',
          sortable: true,
        },
        {
          name: 'total_material_sum',
          label: 'Сумма',
          align: 'right',
          field: 'total_material_sum',
          sortable: true,
        },
      ],
      callsData: [],
      loading: false,
      selectedCallIds: [],
      materialsData: [],
      materialsLoading: false,
      creatingAvr: false,
      /** Предпросмотр только КС2В */
      k2svPreviewTypeCode: 'K2SV',
      downloadingPreview: false,
    }
  },
  computed: {
    selectableCallIds() {
      return this.callsData
        .filter((row) => this.isK2svRowSelectable(row))
        .map((row) => row.help_call_id);
    },
    isAllSelected() {
      const s = this.selectableCallIds;
      return s.length > 0 && s.every((id) => this.selectedCallIds.includes(id));
    },
    isIndeterminate() {
      const s = this.selectableCallIds;
      const n = this.selectedCallIds.filter((id) => s.includes(id)).length;
      return n > 0 && n < s.length;
    },
    selectedCallsSumWithVat() {
      return this.callsData
        .filter(item => this.selectedCallIds.includes(item.help_call_id))
        .reduce((sum, item) => sum + (parseFloat(item.call_sum) || 0), 0);
    },
    selectedCallsSumWithoutVat() {
      return this.callsData
        .filter(item => this.selectedCallIds.includes(item.help_call_id))
        .reduce((sum, item) => sum + (parseFloat(item.call_sum_without_vat) || 0), 0);
    },
    selectedCallsNdsFooter() {
      const rows = this.callsData.filter(item => this.selectedCallIds.includes(item.help_call_id));
      const percents = rows
        .map((r) => r.nds_percent)
        .filter((p) => p !== null && p !== undefined && p !== '');
      if (percents.length === 0) {
        return '—';
      }
      const first = percents[0];
      const same = percents.every((p) => String(p) === String(first));
      return same ? `${first}` : '—';
    },
  },
  watch: {
    selectedCallIds: {
      handler(newIds) {
        if (newIds.length > 0) {
          this.loadMaterials(newIds);
        } else {
          this.materialsData = [];
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      const projectId = this.data?.project_id;
      if (!projectId) {
        this.loading = false;
        return;
      }
      const res = await getManualAvrCalls(projectId);
      this.loading = false;
      if (!res) return;
      this.callsData = res.data || [];
      this.pruneK2svSelection();
    },
    selectAll(value) {
      if (value) {
        this.selectedCallIds = [...this.selectableCallIds];
        if (this.selectedCallIds.length > 0) {
          this.loadMaterials(this.selectedCallIds);
        }
      } else {
        // Снимаем выбор со всех
        this.selectedCallIds = [];
        this.materialsData = [];
      }
    },
    toggleSelectItem(helpCallId, value) {
      const row = this.callsData.find((r) => r.help_call_id === helpCallId);
      if (value && row && !this.isK2svRowSelectable(row)) {
        this.showErrorSnackbar(
          'Нельзя выбрать вызов: не выполнены условия КС2В (опер-факт, НУ, ОКК) или сумма с НДС по вызову не больше 0'
        );
        return;
      }
      const index = this.selectedCallIds.indexOf(helpCallId);
      if (value) {
        if (index === -1) {
          this.selectedCallIds.push(helpCallId);
          // Сразу вызываем загрузку материалов при выборе
          this.loadMaterials(this.selectedCallIds);
        }
      } else {
        if (index > -1) {
          this.selectedCallIds.splice(index, 1);
          // Обновляем материалы при снятии выбора
          if (this.selectedCallIds.length > 0) {
            this.loadMaterials(this.selectedCallIds);
          } else {
            this.materialsData = [];
          }
        }
      }
    },
    isItemSelected(helpCallId) {
      return this.selectedCallIds.includes(helpCallId);
    },
    async loadMaterials(helpCallIds) {
      if (!helpCallIds || helpCallIds.length === 0) {
        this.materialsData = [];
        return;
      }
      this.materialsLoading = true;
      const res = await getManualAvrMaterials(helpCallIds);
      this.materialsLoading = false;
      if (!res) {
        this.materialsData = [];
        return;
      }
      const raw = res.data || [];
      this.materialsData = raw.filter((row) => this.isManualMaterialRowRenderable(row));
    },
    /** Убирает пустые строки из выборки (агрегат без material_name / ед. изм., только нули). */
    isManualMaterialRowRenderable(row) {
      if (!row) return false;
      const name = (row.material_name && String(row.material_name).trim()) || '';
      const unit = (row.material_unit_name && String(row.material_unit_name).trim()) || '';
      if (name || unit) return true;
      const amt = Number(row.material_amount);
      const price = Number(row.material_price);
      const sum = Number(row.total_material_sum);
      const nz = (v) => Number.isFinite(v) && v !== 0;
      return nz(amt) || nz(price) || nz(sum);
    },
    async createAvr() {
      if (this.selectedCallIds.length === 0) {
        this.showErrorSnackbar("Не выбрано ни одного элемента");
        return;
      }
      const bad = this.selectedCallIds.filter((id) => {
        const row = this.callsData.find((r) => r.help_call_id === id);
        return !row || !this.isK2svRowSelectable(row);
      });
      if (bad.length > 0) {
        this.showErrorSnackbar(
          'Среди выбранных есть вызовы без условий КС2В (опер-факт, НУ, ОКК) или с нулевой суммой. Снимите их или обновите список.'
        );
        return;
      }

      this.creatingAvr = true;
      try {
        const res = await createManualAvr(this.selectedCallIds);
        this.creatingAvr = false;
        
        if (!res) {
          return;
        }

        this.showSucceedSnackbar("КС2В успешно создан");
        
        // Закрываем модалку перед обновлением данных
        this.closeModal();
        
        // Вызываем callback для обновления данных после закрытия модалки
        // Используем setTimeout, чтобы убедиться, что модалка закрылась
        if (this.data?.onSuccess) {
          setTimeout(() => {
            this.data.onSuccess();
          }, 100);
        }
      } catch (e) {
        this.creatingAvr = false;
        this.showErrorSnackbar(e?.response?.data?.message || e?.message || "Ошибка при создании КС2В");
      }
    },
    formatNdsPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }
      return String(value);
    },
    isK2svFlagOk(v) {
      return v === true || v === 't' || v === 'true' || v === 1;
    },
    isHelpCallPositiveSum(row) {
      if (!row || row.call_sum === null || row.call_sum === undefined || row.call_sum === '') {
        return false;
      }
      const v = parseFloat(row.call_sum);
      return !Number.isNaN(v) && v > 0;
    },
    isK2svRowSelectable(row) {
      if (!row) return false;
      if (!this.isHelpCallPositiveSum(row)) {
        return false;
      }
      return (
        this.isK2svFlagOk(row.k2sv_operfact_pct_ok)
        && this.isK2svFlagOk(row.k2sv_nu_ok)
        && this.isK2svFlagOk(row.k2sv_okk_accept_ok)
      );
    },
    formatK2svMissing(row) {
      const parts = [];
      if (!this.isHelpCallPositiveSum(row)) {
        parts.push('сумма с НДС по вызову 0');
      }
      if (!this.isK2svFlagOk(row.k2sv_operfact_pct_ok)) {
        parts.push('опер-факт не 100%');
      }
      if (!this.isK2svFlagOk(row.k2sv_nu_ok)) {
        parts.push('нет приёмки НУ');
      }
      if (!this.isK2svFlagOk(row.k2sv_okk_accept_ok)) {
        parts.push('нет приёмки ОКК');
      }
      return parts.length ? parts.join('; ') : '';
    },
    pruneK2svSelection() {
      const allowed = new Set(this.selectableCallIds);
      this.selectedCallIds = this.selectedCallIds.filter((id) => allowed.has(id));
    },
    async openK2svPreviewPdf() {
      if (this.selectedCallIds.length === 0) {
        this.showErrorSnackbar("Не выбрано ни одного элемента");
        return;
      }
      const bad = this.selectedCallIds.filter((id) => {
        const row = this.callsData.find((r) => r.help_call_id === id);
        return !row || !this.isK2svRowSelectable(row);
      });
      if (bad.length > 0) {
        this.showErrorSnackbar(
          'Предпросмотр недоступен: есть выбранные вызовы без условий КС2В или с нулевой суммой.'
        );
        return;
      }

      const typeCode = this.k2svPreviewTypeCode;

      /* window.open только синхронно из клика; до await и до loading — иначе popup-блокировка */
      const previewTab = window.open("about:blank", "_blank");
      if (!previewTab) {
        this.showErrorSnackbar(
          "Разрешите всплывающие окна для этого сайта, чтобы открыть предпросмотр."
        );
        return;
      }

      this.downloadingPreview = true;
      try {
        await openManualPreviewInTab(this.selectedCallIds, typeCode, previewTab);
        this.showSucceedSnackbar("Предпросмотр открыт в новой вкладке");
      } catch (e) {
        this.showErrorSnackbar(e?.message || e?.response?.data?.message || "Ошибка при открытии предпросмотра");
      } finally {
        this.downloadingPreview = false;
      }
    },
  },
  setup() {
    const { closeModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { closeModal, showSucceedSnackbar, showErrorSnackbar };
  },
}
</script>

<style scoped lang="scss">
.manual-avr-modal {
  min-width: 600px;
  max-width: 1200px;
  max-height: min(85vh, 900px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: 0;
  padding-top: 5px;

  &__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__materials {
    margin-top: 0;
  }

  &__dock {
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin-top: 12px;
    padding-top: 16px;
    background: #fff;
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.06);
  }

  &__preview {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  &__preview-label {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }

  :deep(.custom-table) {
    --tbl-radius: 8px !important;
    border-radius: 8px !important;
  }

  :deep(.q-table tbody td) {
    font-size: 12px;
  }

  .k2sv-hints {
    font-size: 11px;
    line-height: 1.35;
    color: #b45309;
  }

  .k2sv-hints--ok {
    color: #15803d;
  }

  .k2sv-hints-cell {
    max-width: 220px;
  }

  &__row--blocked {
    opacity: 0.72;
    cursor: not-allowed;
  }
}
</style>
